import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const TermsAndPrivacy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="max-w-4xl mx-auto p-8 text-white-800 text-left">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 mb-6 text-white-700 hover:text-white-900 transition-colors duration-200"
      >
        <ArrowLeft size={20} />
        <span>Back</span>
      </button>
      <h1 className="mb-8 font-bold text-[2.5rem]">Terms and Privacy Policy</h1>
      <p className="mb-4 mt-6">
        <strong>Effective Date:</strong> 22 May 2025
      </p>

      <p>
        At My!hu, we respect your privacy and are committed to protecting your
        personal data. This Privacy Policy explains how we collect, use,
        disclose, and safeguard your information when you use our Platform.
      </p>

      <h2 className="font-semibold text-lg mb-4 mt-6">
        1. Information We Collect
      </h2>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          <strong>Personal Information: </strong>
          Name, email address, and optional profile details (e.g., username,
          avatar).
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          <strong>Usage Data: </strong>
          Interactions with the Platform, feature preferences, session duration,
          and feedback.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          <strong>Environmental &amp; Emotional Inputs: </strong>
          Location data (only with explicit consent), sensor-derived
          environmental readings (e.g., air quality), and self-reported
          emotional check-ins.
        </li>
      </ul>

      <h2 className="font-semibold text-lg mb-4 mt-6">
        2. How We Use Your Information
      </h2>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Provide, operate, personalize, and improve the Platform’s features and
          user experience.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Generate environmental insights, analytical reports, forecasts,
          summaries, and related outputs requested by users.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Communicate with you regarding service updates, operational notices,
          and account-related information, where you have opted in or where
          required.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Comply with legal obligations, enforce applicable terms, and protect
          the integrity and security of the Platform.
        </li>
      </ul>

      <h2 className="font-semibold text-lg mb-4 mt-6">
        3. Data Sharing and Disclosure
      </h2>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          We do not sell, rent, or trade your personal data.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          We may share aggregated or anonymized data with academic institutions,
          environmental organizations, or public-interest entities for research,
          analysis, or reporting purposes.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          We may disclose personal data where required by law, regulatory
          obligation, or to protect the rights, safety, or security of My!hu,
          its users, or the public.
        </li>
      </ul>

      <h2 className="font-semibold text-lg mb-4 mt-6">
        4. Your Rights and Choices
      </h2>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Access, correct, export, or delete your personal data.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Restrict or object to certain processing activities, where applicable.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Withdraw consent where processing is based on consent.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Opt out of non-essential communications or optional data collection
          features.
        </li>
      </ul>
      <p>
        You may exercise these rights through your account settings or by
        contacting us at
        <strong> info@myhu.com</strong>.
      </p>

      <h2 className="font-semibold text-lg mb-4 mt-6">5. Data Security</h2>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          We implement appropriate technical and organizational safeguards,
          including encryption in transit and at rest, access controls, and
          secure cloud infrastructure.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          While no system can be guaranteed to be completely secure, we will
          notify affected users of any material data breach as required by
          applicable law.
        </li>
      </ul>

      <h2 className="font-semibold text-lg mb-4 mt-6">6. Policy Updates</h2>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          We may update this Privacy Policy from time to time to reflect changes
          in legal requirements, operational practices, or service offerings.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Material changes will be communicated via in-platform notice, email,
          or other reasonable means, along with an updated effective date.
        </li>
      </ul>

      <h2 className="font-semibold text-lg mb-4 mt-6">
        7. Third-Party Integrations
      </h2>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          We may share limited, necessary data with trusted service providers
          solely to enable platform functionality (e.g., hosting, storage,
          analytics, or processing services).
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Such data sharing is purpose-limited, contractually restricted, and
          subject to appropriate security safeguards.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Third-party providers are prohibited from using user data for
          advertising, profiling, or unrelated commercial purposes.
        </li>
      </ul>

      <h2 className="font-semibold text-lg mb-4 mt-6">8. Embedded Services</h2>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Certain features may rely on embedded third-party services (such as
          mapping, visualization, or media components).
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          These services may independently collect usage or technical data in
          accordance with their own privacy policies.
        </li>
      </ul>

      <h2 className="font-semibold text-lg mb-4 mt-6">
        9. Consent and Transparency
      </h2>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          We provide clear notice where third-party processing is involved and
          obtain consent where required by applicable law.
        </li>
      </ul>

      <h2 className="font-semibold text-lg mb-4 mt-6">
        10. Payment Models, Credits, and Billing
      </h2>

      <h3 className="font-semibold text-sm mb-4 mt-6">
        10.1 Pay-As-You-Go (PAYG) Credits
      </h3>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          My!hu offers a Pay-As-You-Go model that allows users to purchase
          prepaid usage-based credits.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Credits are deducted only upon the successful generation of a
          completed output requested by the user, such as a report, forecast,
          prediction run, or data export.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Credits are not consumed for browsing, viewing previously generated
          outputs, managing projects, or accessing account settings.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          If an output fails to generate due to a system error, unavailable
          data, or platform fault, the associated credits will not be deducted.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Applicable credit requirements and usage costs are disclosed to users
          prior to initiating a credit-consuming action.
        </li>
      </ul>

      <h3 className="font-semibold text-sm mb-4 mt-6">
        10.2 Credit Validity and Expiry
      </h3>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Purchased credits do not expire unless explicitly stated at the time
          of purchase or required by applicable law.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Where expiry applies, credits will remain valid for a minimum of
          twenty-four (24) months from the date of purchase.
        </li>
      </ul>

      <h3 className="font-semibold text-sm mb-4 mt-6">
        10.3 Professional Annual Plan
      </h3>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          My!hu offers a Professional plan billed annually in advance.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          The Professional plan provides enhanced usage allowances, access to
          advanced features, and priority service levels intended for regular or
          professional use.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          The Professional plan is not billed on a monthly recurring basis.
        </li>
      </ul>

      <h3 className="font-semibold text-sm mb-4 mt-6">
        10.4 Ownership of Outputs
      </h3>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Outputs generated through PAYG credits or under the Professional plan
          are owned by the user.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Cancellation, expiration, or termination of a plan does not revoke
          access to outputs already generated, subject to applicable data
          retention and legal obligations.
        </li>
      </ul>

      <h3 className="font-semibold text-sm mb-4 mt-6">
        10.5 Refunds and Billing Errors
      </h3>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Payments for credits and annual plans are non-refundable once
          processed, except where required by applicable law.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Billing errors may be reviewed if reported within fourteen (14) days
          of the transaction date.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Approved refunds, where applicable, will be issued to the original
          payment method.
        </li>
      </ul>

      <h3 className="font-semibold text-sm mb-4 mt-6">
        10.6 Plan Changes and Pricing Adjustments
      </h3>
      <ul>
        <li className="list-disc pl-6 space-y-2 ml-4">
          Users may purchase additional credits or upgrade plans at any time
          through account settings.
        </li>
        <li className="list-disc pl-6 space-y-2 ml-4">
          My!hu reserves the right to adjust credit pricing, usage requirements,
          or plan features prospectively. Such changes will not affect credits
          already purchased or outputs already generated.
        </li>
      </ul>

      <h2 className="font-semibold text-lg mb-4 mt-6">Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy or billing matters,
        please contact us at:
      </p>
      <p>
        <strong>support@myhu.world</strong>
      </p>
    </div>
  );
};

export default TermsAndPrivacy;
